import { useCallback, useEffect, useState } from "react";

export type UseDisclosureProps = {
  isOpen?: boolean;
  isDefaultOpen?: boolean;
  onOpen?(): void;
  onClose?(): void;
};

export type DisclosureState = {
  isOpen: boolean;
  /**
   * Identifies a single open episode. Always-mounted drawers key on this so
   * they remount with fresh state every time they open, and keep a stable key
   * while they close so the exit animation can finish.
   */
  session: number;
};

/**
 * A new session per open episode, so anything keyed on it remounts on open and
 * only on open. Re-opening an already-open disclosure keeps the session, or
 * whatever is keyed on it would remount mid-flight.
 */
export const nextDisclosureState = (
  prev: DisclosureState,
  isOpen: boolean
): DisclosureState => {
  if (isOpen === prev.isOpen) {
    return prev;
  }
  return isOpen
    ? { isOpen: true, session: prev.session + 1 }
    : { ...prev, isOpen: false };
};

export function useDisclosure(props: UseDisclosureProps = {}) {
  const { isOpen: isOpenProp, isDefaultOpen, onOpen, onClose } = props;

  const [{ isOpen, session }, setState] = useState<DisclosureState>({
    isOpen: isDefaultOpen || false,
    session: 0,
  });

  useEffect(() => {
    setState((prev) => nextDisclosureState(prev, !!isOpenProp));
  }, [isOpenProp]);

  const open = useCallback(
    (e?: any) => {
      if (e && e["stopPropagation"]) {
        // if e is a mouse event and has stopPropagation, call it.
        e.stopPropagation();
      }
      setState((prev) => nextDisclosureState(prev, true));
      onOpen && onOpen();
    },
    [onOpen]
  );

  const close = useCallback(
    (e?: any) => {
      if (e && e["stopPropagation"]) {
        // if e is a mouse event and has stopPropagation, call it.
        e.stopPropagation();
      }
      setState((prev) => nextDisclosureState(prev, false));
      onClose && onClose();
    },
    [onClose]
  );

  const toggle = useCallback(
    (e?: any) => {
      if (isOpen) {
        close(e);
      } else {
        open(e);
      }
    },
    [isOpen, open, close]
  );

  return { isOpen, session, open, close, toggle };
}

type UseDisclosureReturn = ReturnType<typeof useDisclosure>;
